// NeedsYou.tsx — the "is there anything I need to deal with" half of the Overview screen's
// 60-second session (handoff README §2 "Work" row / task-8-brief.md Step 2). Promotes the top
// 3 `topUnanswered` groups the `BotOverviewDto` already carries onto the home screen with a
// `Write answer` action per row.
//
// Deliberately duplicates part of BotUnanswered.tsx: read-only question + one action, capped at
// three. Reviewed and kept by Johnny 2026-08-08 (handoff README §2) — Overview is meant to read
// as a workplace before the owner ever opens the Unanswered tab, not a summary card that defers
// everything to it.
//
// `Write answer` reuses the EXISTING `useAnswerUnanswered` mutation (hooks/useUnanswered.ts)
// rather than a bespoke one — it already force-invalidates the `['bots', botId, 'overview']`
// query on success, so a successful answer here shrinks `topUnanswered` and the rail's amber
// pill on the very next render with no extra invalidation wiring. That also means (unlike
// BotUnanswered.tsx, which deliberately freezes an answered row in place with a success line)
// an answered row here just drops out of the list once the overview refetch lands — this panel
// is meant to update "immediately" per the brief, not preserve a completed-row artifact.
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@repo/ui/components/button';
import type { UnansweredGroupDto } from '@repo/shared-types';
import { useAnswerUnanswered } from '../../hooks/useUnanswered';
import { relativeTime } from '../../lib/relative-time';
import { useT } from '../../locales/context';
import type { Catalog } from '../../locales/en';

function NeedsYouRow({
  group,
  isFirst,
  isOpen,
  answer,
  onToggle,
  onAnswerChange,
  onSubmit,
  isSubmitting,
  error,
  t,
}: {
  group: UnansweredGroupDto;
  isFirst: boolean;
  isOpen: boolean;
  answer: string;
  onToggle: () => void;
  onAnswerChange: (value: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  error?: string;
  t: Catalog;
}) {
  return (
    <div className={`flex items-stretch gap-3.5 px-5 py-3 ${isFirst ? '' : 'border-t'}`}>
      <span aria-hidden="true" className="w-[3px] shrink-0 self-stretch rounded-full bg-st-paused" />
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-semibold">{group.question}</span>
            <span className="mt-0.5 text-xs text-muted-foreground">
              {t.overview.askedSummary({ n: group.count, time: relativeTime(group.lastAskedAt, t.time) })}
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-[30px] flex-none"
            onClick={onToggle}
            disabled={isSubmitting}
          >
            {isOpen ? t.overview.cancelCta : t.overview.writeAnswerCta}
          </Button>
        </div>

        {isOpen && (
          <div className="flex flex-col gap-2">
            <textarea
              rows={3}
              value={answer}
              onChange={(e) => onAnswerChange(e.target.value)}
              placeholder={t.overview.answerPlaceholder}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            />
            <div className="flex gap-2">
              <Button type="button" size="sm" disabled={!answer.trim() || isSubmitting} onClick={onSubmit}>
                {isSubmitting ? t.overview.addingCta : t.overview.addAsQaSourceCta}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={onToggle}>
                {t.overview.cancelCta}
              </Button>
            </div>
            {error && <p className="text-[12.5px] text-destructive">{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export function NeedsYou({
  botId,
  items,
  totalCount,
}: {
  botId: string | undefined;
  /** `BotOverviewDto.topUnanswered` — at most 3, already ordered server-side. */
  items: UnansweredGroupDto[];
  /** `BotOverviewDto.totals.unansweredGroups` — the real total, which can exceed `items.length`
   * (the header pill and stat card must agree with the rail's count even when there are more
   * than 3 open groups). */
  totalCount: number;
}) {
  const t = useT();
  const answerMutation = useAnswerUnanswered(botId);

  // A single open editor at a time is the realistic case, but nothing enforces it — keyed by
  // `group.normalized` the same way BotUnanswered.tsx tracks per-row state.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<{ key: string; message: string } | null>(null);

  function toggle(key: string) {
    setOpenKey((prev) => (prev === key ? null : key));
    setSubmitError((prev) => (prev?.key === key ? null : prev));
  }

  function submit(group: UnansweredGroupDto) {
    const answer = (drafts[group.normalized] ?? '').trim();
    if (!answer) return;
    setSubmitError(null);
    setSubmittingKey(group.normalized);
    answerMutation.mutate(
      { question: group.question, answer },
      {
        onSuccess: () => {
          setSubmittingKey(null);
          setOpenKey(null);
        },
        onError: () => {
          setSubmittingKey(null);
          setSubmitError({ key: group.normalized, message: t.overview.saveAnswerError });
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-0 overflow-hidden rounded-[14px] border bg-card shadow-el-1">
      <div className="flex items-center justify-between gap-2.5 px-5 pt-[15px] pb-3">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-lg font-semibold">{t.overview.needsYouHeading}</h2>
          {totalCount >= 1 && (
            <span
              className="inline-grid h-[19px] min-w-[19px] place-items-center rounded-full px-1.5 text-[11px] font-semibold text-st-paused"
              style={{ backgroundColor: 'color-mix(in oklch, var(--st-paused) var(--st-wash), transparent)' }}
            >
              {totalCount}
            </span>
          )}
        </div>
        <Link to={`/bots/${botId}/unanswered`} className="text-[12.5px] font-medium text-primary hover:underline">
          {t.overview.allUnansweredLink}
        </Link>
      </div>

      {items.length === 0 ? (
        <p className="px-5 pb-5 text-[13px] text-muted-foreground">{t.overview.needsYouEmpty}</p>
      ) : (
        <>
          <p className="px-5 pb-2 text-[12.5px] text-muted-foreground">{t.overview.needsYouIntro}</p>
          <div className="flex flex-col">
            {items.map((group, i) => (
              <NeedsYouRow
                key={group.normalized}
                group={group}
                isFirst={i === 0}
                isOpen={openKey === group.normalized}
                answer={drafts[group.normalized] ?? ''}
                onToggle={() => toggle(group.normalized)}
                onAnswerChange={(value) => setDrafts((prev) => ({ ...prev, [group.normalized]: value }))}
                onSubmit={() => submit(group)}
                isSubmitting={submittingKey === group.normalized}
                error={submitError?.key === group.normalized ? submitError.message : undefined}
                t={t}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
